import React, { useContext, useEffect, useState } from "react";
import { ThreeRow, TwoColumn, TwoRow } from "../../core/layouts";
import { Tree, Text, Space, Tooltip } from "react_reusable_components";
import variables from "../../themes/base/styles/variables";
import Style from "./QuestionnaireNavigationContainerStyle.module.scss";
import { AuditContext } from "../../globalContext/ManageAudit/AuditProvider";
import { formatDate, getLocalizedMessage } from "../../UtilityFunctions/utils";
import { useIntl } from "react-intl";
import { userRole } from "../../constants";
import { AuthContext } from "../../globalContext/auth/authProvider";
import useResponsive from "../../core/hooks/useResponsive";

const QuestionnaireNavigationContainerStyle = {
  mainThreeRowWrapper: {
    display: "flex",
    gap: variables.gapLarge1,
    padding: `${variables.paddingSmall} 0`,
    backgroundColor: variables.bodyBg,
    border: `1px solid ${variables.grey4}`,
    width: variables.questionnaireNavigationContainerWidth,
    flex: "0 0 auto",
    height: variables.questionnaireNavigationContainerHeight,
    overflow: "unset",
  },
  hamburgerIcon: {
    cursor: "pointer",
    width: variables.smallIconWidth,
    height: variables.smallIconHeight,
  },
  middleSectionStyle: {
    padding: `0 ${variables.paddingSmall}`,
    alignItems: "center",
  },
  bottomSectionStyle: {
    width: "100%",
    overflowY: "auto",
    padding: 0,
    // height: variables.questionnaireNavigationTreeHeight,
    flex: 1,
    paddingLeft: "calc(1rem - 6px)",
  },
  mainTwoRowStyles: {
    display: "flex",
    gap: variables.gapXS1,
    width: variables.questionnaireNavigationContainerWidth,
    flex: "0 0 auto",
    height: variables.questionnaireNavigationContainerHeight,
    overflow: "unset",
  },
  topWrapperStyle: (responsive) => ({
    alignItems: variables.alignCenter,
    gap: responsive.isSm && variables.gapSmaller,
    justifyContent: !responsive.isSm && "right",
  }),
  iconWrapperStyle: {
    backgroundColor: variables.bodyBg,
    padding: variables.paddingSmall,
    border: `1px solid ${variables.grey4}}`,
    cursor: "pointer",
  },
  displayNoneStyle: {
    display: "none",
  },
  auditInstancesWrapperStyle: {
    flexDirection: "column",
    overflowY: "auto",
    gap: variables.gapXS1,
    height: "calc(100% - 3.5rem)",
  },
  auditWrapperStyle: {
    cursor: "pointer",
    backgroundColor: variables.bodyBg,
    border: `1px solid ${variables.grey4}`,
  },
  headingStyle: {
    color: variables.grey2,
    fontSize: variables.fontSizeSmall,
    lineHeight: variables.lineHeightNormal,
    fontWeight: variables.fontWeightNormal,
  },
  dateStyle: {
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightNormal,
    fontWeight: variables.fontWeightNormal,
  },
  auditBottomSectionStyle: {
    padding: variables.paddingSmall,
    paddingRight: "1px", // just give an space on green border and red scroll bar
    flex: 1,
    overflow: "hidden",
  },
  questionWrapperStyle: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "24px", // keep it constant in px for tree internal style
    gap: variables.gapXS,
    marginTop: variables.marginHalf,
  },
};

const scrollIntoView = (id) => {
  document.getElementById(id).scrollIntoView();
};

function findFirstQuestionIdBySectionSequence(node, targetSectionSequence) {
  if (node.questions && node.questions.length > 0) {
    return node.questions[0].id;
  }

  if (node.sections && node.sections.length > 0) {
    for (const childNode of node.sections) {
      const result = findFirstQuestionIdBySectionSequence(
        childNode,
        targetSectionSequence
      );
      if (result) {
        return result;
      }
    }
  }
  return null;
}
const NodeTitle = ({
  node,
  handleSetExpandedKeys,
  questionsData,
  setSelected,
}) => {
  const isRemote = window.location.pathname.includes("remote");
  const [authState] = useContext(AuthContext);
  const onSelect = (question) => {
    if (isRemote) {
      if (authState?.currentRole?.id !== userRole.TM) return;
      setSelected(
        question
          ? question.id
          : findFirstQuestionIdBySectionSequence(node, node.sequence)
      );
    } else {
      handleSetExpandedKeys(questionsData);
      setTimeout(
        () =>
          scrollIntoView(
            question
              ? `${node.sequence}${question.sequence}`
              : `${node.sequence}`
          ),
        700
      );
    }
  };
  return (
    <>
      <Text
        className="title-renderer"
        onClick={() => {
          onSelect(false);
        }}
        style={{
          ...(isRemote &&
            authState?.currentRole?.id !== userRole.TM && {
              cursor: "default",
            }),
        }}
      >
        {node.key}. &nbsp;
        <Tooltip placement="bottomLeft" title={node.sectionName}>
          <span style={{ textTransform: "capitalize" }}>
            {node.sectionName}
          </span>
        </Tooltip>
      </Text>
      {node?.questions && (
        <div style={QuestionnaireNavigationContainerStyle.questionWrapperStyle}>
          {node?.questions?.map((question, index) => {
            let questionColor;
            if (question?.questionResponse?.evaluationStatus === "OK") {
              questionColor = variables.successGreen;
            } else if (
              question?.questionResponse?.evaluationStatus === "IMPROVEMENT"
            ) {
              questionColor = variables.amberYellow;
            } else if (question?.questionResponse?.evaluationStatus === "NG") {
              questionColor = variables.primaryBg;
            }
            const url = new URL(window.location.href);
            const path = url.pathname;
            const isAudit = path.includes("audit");
            return (
              <div
                key={question?.sequence || question?.id}
                onClick={() => {
                  onSelect(question);
                }}
                style={{
                  display: variables.dFlex,
                  ...(isRemote &&
                    authState?.currentRole?.id !== userRole.TM && {
                      cursor: "default",
                    }),
                }}
              >
                <div
                  className={
                    question.name &&
                    `${"nav-question"} ${index !== 0 && "nav-question-next"}`
                  }
                ></div>
                <Text
                  className="title-renderer"
                  style={
                    isAudit && {
                      color: questionColor,
                    }
                  }
                >
                  <span style={{ textTransform: "none" }}>
                    {String.fromCharCode(96 + question.sequence)}
                  </span>
                  . &nbsp;
                  <Tooltip
                    placement={!isRemote ? "bottomLeft" : undefined}
                    title={question.name}
                  >
                    <span style={{ textTransform: "capitalize" }}>
                      {question?.isMandatory
                        ? `* ${question.name}`
                        : question.name}
                    </span>
                  </Tooltip>
                </Text>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
const QuestionnaireNavigationContainer = ({
  questionnaireName,
  questionsData,
  expandedKeys,
  mode,
  openNavigation,
  setOpenNavigation,
  selectedAuditInstance,
  setSelectedAuditInstance,
  setSelected,
  absolute,
  handleSetExpandedKeys,
}) => {
  const isAuditPreview = window.location.href.includes("preview");
  const [auditState] = useContext(AuditContext);
  const [auditInstances, setAuditInstances] = useState([]);
  const intl = useIntl();
  const treeDataCopy = JSON.parse(JSON.stringify(questionsData)) || [];
  const modifiedTreeData = treeDataCopy.map((node) => mapTreeData(node));
  const responsive = useResponsive();

  function mapTreeData(node) {
    // Modify the title property with a button component
    node.title = (
      <NodeTitle
        node={node}
        handleSetExpandedKeys={handleSetExpandedKeys}
        questionsData={questionsData}
        setSelected={setSelected}
      />
    );
    node.key = node.sequence;

    // If the node has children, recursively call the mapTreeData function on each child
    if (node?.sections) {
      node.children = node?.sections?.map((child) => mapTreeData(child));
    }
    return node;
  }

  useEffect(() => {
    setAuditInstances(auditState?.auditDetail);
  }, [auditState]);

  const totalAudits = auditState?.auditDetail?.length;

  const getTopSection = () => {
    if (!absolute || !openNavigation) {
      return (
        <img
          alt="icon"
          src="/icons/hamburger.svg"
          onClick={() => setOpenNavigation((prev) => !prev)}
          style={QuestionnaireNavigationContainerStyle.hamburgerIcon}
        />
      );
    }

    return (
      <TwoColumn
        style={{ justifyContent: "space-between", alignItems: "center" }}
        leftSection={
          auditState?.auditDetail?.reAuditDueDate && (
            <TwoColumn
              style={{
                alignSelf: "end",
              }}
              leftSection={
                <Text className={Style["date-heading"]}>
                  {getLocalizedMessage(intl, "mangeAudit.F2F.auditDueHeading")}
                  &nbsp;:&nbsp;
                </Text>
              }
              rightSection={
                <Text className={Style["questionnaire-type"]} type="secondary">
                  {auditState?.auditDetail?.reAuditDueDate
                    ? formatDate(auditState?.auditDetail?.reAuditDueDate, intl)
                    : "-"}
                </Text>
              }
            />
          )
        }
        rightSection={
          <img
            style={{
              width: variables.iconWidth,
              height: variables.iconHeight,
              cursor: "pointer",
            }}
            src={"/icons/close.svg"}
            alt="block-user"
            onClick={() => setOpenNavigation(false)}
          />
        }
      />
    );
  };

  return isAuditPreview ? (
    <TwoRow
      style={{
        ...(openNavigation &&
          QuestionnaireNavigationContainerStyle.mainTwoRowStyles),
        ...((mode === "view" || isAuditPreview) && {
          height: variables.questionnaireNavigationContainerHeightSmall,
        }),
        ...(absolute && {
          position: "absolute",
          zIndex: 99,
          top: "1.375rem",
          right: openNavigation ? "1rem" : "1.5rem",
          gap: variables.paddingSmall,
          height: openNavigation && "calc(100% - 6.25rem)",
        }),
      }}
      topSection={
        <TwoColumn
          style={QuestionnaireNavigationContainerStyle.topWrapperStyle(
            responsive
          )}
          leftSection={
            <Space
              size={0}
              style={QuestionnaireNavigationContainerStyle.iconWrapperStyle}
              onClick={() => setOpenNavigation((prev) => !prev)}
            >
              <img
                alt="icon"
                src="/icons/hamburger.svg"
                style={{
                  ...QuestionnaireNavigationContainerStyle.hamburgerIcon,
                }}
              />
            </Space>
          }
          rightSectionStyle={{
            ...(!openNavigation ||
              (absolute &&
                QuestionnaireNavigationContainerStyle.displayNoneStyle)),
            width: "calc(100% - 5rem)",
          }}
          rightSection={
            openNavigation && (
              <Text
                className={`${Style["heading-text"]} ${
                  Style[!questionnaireName && "placeholder-style"]
                }`}
              >
                {questionnaireName || "Untitled"}
              </Text>
            )
          }
        />
      }
      bottomSectionStyle={{
        ...QuestionnaireNavigationContainerStyle.auditInstancesWrapperStyle,
        ...(!openNavigation &&
          QuestionnaireNavigationContainerStyle.displayNoneStyle),
        overflow: "unset",
      }}
      bottomSection={auditInstances?.map((auditInstance, index) => {
        return (
          <TwoRow
            onClick={() => {
              setSelectedAuditInstance(index);
            }}
            key={auditInstance}
            style={{
              ...QuestionnaireNavigationContainerStyle.auditWrapperStyle,
              flexGrow: index !== selectedAuditInstance && "unset",
              minHeight: variables.previewMinHeight,
              justifyContent: "center",
              // overflow: "unset",
              border:
                index === selectedAuditInstance &&
                totalAudits > 1 &&
                `1px solid ${variables.successGreen}`,
            }}
            topSectionStyle={{
              ...(totalAudits > 1 && {
                padding: variables.paddingSmall,
              }),
              ...(totalAudits === 1 &&
                QuestionnaireNavigationContainerStyle.displayNoneStyle),
            }}
            topSection={
              <TwoRow
                topSection={
                  <Text
                    style={{
                      ...QuestionnaireNavigationContainerStyle.headingStyle,

                      color:
                        selectedAuditInstance === index &&
                        totalAudits > 1 &&
                        variables.successGreen,
                    }}
                  >
                    {getLocalizedMessage(
                      intl,
                      getMessageData(index, totalAudits).messageKey,
                      { index: getMessageData(index, totalAudits).indexValue }
                    )}
                  </Text>
                }
                bottomSectionStyle={{
                  ...(index === totalAudits - 1 &&
                    QuestionnaireNavigationContainerStyle.displayNoneStyle),
                }}
                bottomSection={
                  <Text
                    style={QuestionnaireNavigationContainerStyle.dateStyle}
                    type="secondary"
                  >
                    {formatDate(
                      auditState?.auditDetail[index]?.reAuditDueDate,
                      intl
                    )}
                  </Text>
                }
              />
            }
            bottomSectionStyle={{
              ...(index === selectedAuditInstance && {
                ...QuestionnaireNavigationContainerStyle.auditBottomSectionStyle,
                borderTop: totalAudits > 1 && `1px solid ${variables.grey4}`,
              }),
            }}
            bottomSection={
              openNavigation &&
              index === selectedAuditInstance &&
              modifiedTreeData && (
                <Tree
                  treeData={modifiedTreeData}
                  type="navigation"
                  externalExpandedKeys={expandedKeys}
                />
              )
            }
          />
        );
      })}
    />
  ) : (
    <ThreeRow
      style={{
        ...(openNavigation &&
          QuestionnaireNavigationContainerStyle.mainThreeRowWrapper),
        ...((mode === "view" || isAuditPreview) && {
          height: variables.questionnaireNavigationContainerHeightSmall,
        }),
        ...(absolute && {
          position: "absolute",
          zIndex: 99,
          top: "1.375rem",
          right: openNavigation ? "1rem" : "1.5rem",
          gap: variables.paddingSmall,
          height: openNavigation && "calc(100% - 6.25rem)",
        }),
      }}
      topSectionStyle={{
        padding: (!absolute || openNavigation) && `0 ${variables.paddingSmall}`,
        borderBottom:
          absolute && openNavigation && `1px solid ${variables.grey4}`,
        paddingBottom: absolute && openNavigation && variables.paddingSmall,
      }}
      topSection={getTopSection()}
      middleSectionStyle={
        QuestionnaireNavigationContainerStyle.middleSectionStyle
      }
      middleSection={
        openNavigation && (
          <Text
            className={`${Style["heading-text"]} ${
              Style[!questionnaireName && "placeholder-style"]
            }`}
          >
            {questionnaireName || "Untitled"}
          </Text>
        )
      }
      bottomSectionStyle={{
        ...QuestionnaireNavigationContainerStyle.bottomSectionStyle,
        display: !openNavigation && "none",
      }}
      bottomSection={
        openNavigation &&
        modifiedTreeData && (
          <Tree
            treeData={modifiedTreeData}
            type="navigation"
            externalExpandedKeys={expandedKeys}
          />
        )
      }
    />
  );
};

export default QuestionnaireNavigationContainer;

function getMessageData(index, totalAudits) {
  if (index === 0 && totalAudits > 1) {
    return {
      messageKey: "auditListing.currentAuditDueDate",
      indexValue: index,
    };
  } else if (index === totalAudits - 1) {
    return {
      messageKey: "auditPreview:firstAudit",
      indexValue: 1,
    };
  } else {
    return {
      messageKey: "auditPreview.reAuditDueDate",
      indexValue: totalAudits - index - 1,
    };
  }
}
